import clsx from "clsx";
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
} from "react";
import { Icon } from "./Icon";

export function Card({
  className,
  children,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) {
  return (
    <div className={clsx("glass-panel rounded-3xl", className)} {...props}>
      {children}
    </div>
  );
}

export function Button({
  className,
  variant = "primary",
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger" | "gold";
}) {
  const styles = {
    primary:
      "bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-400 hover:to-violet-500",
    secondary:
      "border border-slate-700 bg-slate-900/75 text-slate-200 hover:border-slate-500 hover:bg-slate-800",
    ghost: "text-slate-300 hover:bg-slate-800/80 hover:text-white",
    danger:
      "border border-rose-500/30 bg-rose-950/70 text-rose-200 hover:bg-rose-900/80",
    gold: "bg-gradient-to-r from-amber-300 to-orange-400 text-slate-950 shadow-lg shadow-amber-950/30 hover:from-amber-200 hover:to-orange-300",
  };
  return (
    <button
      className={clsx(
        "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-extrabold transition disabled:cursor-not-allowed disabled:opacity-45",
        styles[variant],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function Pill({
  children,
  tone = "indigo",
  className,
}: {
  children: ReactNode;
  tone?: "indigo" | "emerald" | "amber" | "rose" | "slate" | "cyan";
  className?: string;
}) {
  const styles = {
    indigo: "border-indigo-400/20 bg-indigo-500/10 text-indigo-200",
    emerald: "border-emerald-400/20 bg-emerald-500/10 text-emerald-200",
    amber: "border-amber-400/20 bg-amber-500/10 text-amber-200",
    rose: "border-rose-400/20 bg-rose-500/10 text-rose-200",
    slate: "border-slate-600/40 bg-slate-800/70 text-slate-300",
    cyan: "border-cyan-400/20 bg-cyan-500/10 text-cyan-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold",
        styles[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  detail,
  action,
}: {
  eyebrow?: string;
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow && (
          <p className="mb-2 font-retro text-[9px] uppercase tracking-[0.2em] text-cyan-300">
            {eyebrow}
          </p>
        )}
        <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
          {title}
        </h1>
        {detail && (
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-400">
            {detail}
          </p>
        )}
      </div>
      {action}
    </div>
  );
}

export function ProgressBar({
  value,
  color = "bg-indigo-500",
  label,
}: {
  value: number;
  color?: string;
  label?: string;
}) {
  return (
    <div>
      {label && (
        <div className="mb-1.5 flex justify-between text-xs text-slate-400">
          <span>{label}</span>
          <span className="font-bold text-slate-200">{Math.round(value)}%</span>
        </div>
      )}
      <div
        className="h-2.5 overflow-hidden rounded-full border border-slate-800 bg-slate-950"
        role="progressbar"
        aria-valuenow={Math.round(value)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={clsx("h-full rounded-full transition-[width] duration-500", color)}
          style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        />
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  detail,
  action,
}: {
  icon: string;
  title: string;
  detail: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex min-h-56 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-8 text-center">
      <div className="mb-4 rounded-2xl bg-slate-800 p-4 text-slate-400">
        <Icon name={icon} size={28} />
      </div>
      <h3 className="font-display text-xl font-bold text-white">{title}</h3>
      <p className="mt-2 max-w-md text-sm text-slate-400">{detail}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
